import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { ClampedText } from '../common/ClampedText';
import { alpha, tokens } from '../../theme/tokens';
import { usePrefersReducedMotion } from '../../motion/transitions';
import {
  alertHasContentDeepLink,
  isStoryGone,
  resolveAlertOpenTarget,
  storyGoneMessage,
} from '../../utils/alertOpen';
import { alertKindLabel, displayTitle } from '../../utils/labels';

const KIND_ACCENT = {
  standard: tokens.primary,
  banner: tokens.brandFill,
  audio: tokens.inkMuted,
  video: tokens.brandFill,
  actionable: tokens.brandFill,
};

function actionOpensContent(action) {
  return (
    action?.actionType === 'open_article' || action?.actionType === 'open_url'
  );
}

function stopCardClick(e) {
  e.stopPropagation();
}

export function AlertCard({ item, onOpen, onAction, index = 0 }) {
  const accent = KIND_ACCENT[item.kind] || KIND_ACCENT.standard;
  const unread = !item.read;
  const reduceMotion = usePrefersReducedMotion();
  const delayMs = reduceMotion ? 0 : Math.min(index, 8) * 55;
  const actions = item.actions ?? [];
  const storyGone = isStoryGone(item);
  const contentAction = storyGone
    ? undefined
    : actions.find(actionOpensContent);
  const otherActions = actions.filter((a) => !actionOpensContent(a));
  const hasContentLink = !storyGone && alertHasContentDeepLink(item);
  const openTarget = resolveAlertOpenTarget(item);
  const canOpenCard =
    storyGone ||
    hasContentLink ||
    Boolean(contentAction) ||
    openTarget.type === 'play_media';

  function handleCardClick() {
    if (!canOpenCard) return;
    if (contentAction) {
      onAction?.(item, contentAction);
      return;
    }
    onOpen?.(item);
  }

  return (
    <Box
      data-alert-id={item.id}
      role={canOpenCard ? 'button' : undefined}
      tabIndex={canOpenCard ? 0 : undefined}
      onClick={canOpenCard ? handleCardClick : undefined}
      onKeyDown={
        canOpenCard
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardClick();
              }
            }
          : undefined
      }
      sx={{
        borderRadius: { xs: 2, sm: 3 },
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: unread ? tokens.brandFill : 'divider',
        borderWidth: unread ? 1.5 : 1,
        boxShadow: unread
          ? `0 8px 22px ${alpha(tokens.shellDeep, 0.18)}`
          : `0 6px 18px ${alpha(tokens.shellDeep, 0.12)}`,
        color: tokens.ink,
        position: 'relative',
        cursor: canOpenCard ? 'pointer' : 'default',
        '@keyframes prasaarCardIn': {
          from: {
            transform: reduceMotion ? 'none' : 'translateY(8px)',
          },
          to: { transform: 'none' },
        },
        animation: `prasaarCardIn ${reduceMotion ? 160 : 360}ms cubic-bezier(0.16, 1, 0.3, 1)`,
        animationDelay: `${delayMs}ms`,
        transition: 'transform 280ms ease, box-shadow 320ms ease, border-color 280ms ease',
        '@media (hover: hover)': {
          '&:hover': {
            transform: reduceMotion || !canOpenCard ? 'none' : 'translateY(-2px)',
            borderColor: canOpenCard ? tokens.brandFill : undefined,
          },
        },
        '&:focus-visible': {
          outline: `2px solid ${tokens.brand}`,
          outlineOffset: 2,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: unread ? 5 : 4,
          bgcolor: accent,
        },
      }}
    >
      {item.imageUrl && item.kind !== 'audio' && item.kind !== 'video' && (
        <Box
          component="img"
          src={item.imageUrl}
          alt=""
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: { xs: 140, sm: 200 },
            objectFit: 'cover',
          }}
        />
      )}

      <Box sx={{ p: { xs: 2, sm: 2.5 }, pl: { xs: 2.25, sm: 2.75 } }}>
        {(item.kind && item.kind !== 'standard') || unread || storyGone ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 0.75, minHeight: 22 }}
          >
            {item.kind && item.kind !== 'standard' ? (
              <Chip
                size="small"
                label={alertKindLabel(item.kind)}
                sx={{
                  bgcolor: accent,
                  color: 'common.white',
                  fontWeight: 600,
                  height: 28,
                  fontSize: '0.75rem',
                }}
              />
            ) : storyGone ? (
              <Chip
                size="small"
                label="Taken down"
                sx={{
                  height: 30,
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  bgcolor: '#F8E8EA',
                  color: tokens.danger,
                }}
              />
            ) : (
              <Box />
            )}
            <Stack direction="row" spacing={0.5} flexShrink={0}>
              {storyGone && item.kind && item.kind !== 'standard' ? (
                <Chip
                  size="small"
                  label="Taken down"
                  sx={{
                    height: 30,
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    bgcolor: '#F8E8EA',
                    color: tokens.danger,
                  }}
                />
              ) : null}
              {unread ? (
                <Chip
                  size="small"
                  color="secondary"
                  label="Unread"
                  sx={{ height: 28 }}
                />
              ) : null}
            </Stack>
          </Stack>
        ) : null}

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ mb: 0.35 }}
        >
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.05rem', sm: '1.2rem' },
              lineHeight: 1.3,
              overflowWrap: 'break-word',
            }}
          >
            {displayTitle(item.title)}
          </Typography>
          {hasContentLink ? (
            <ChevronRightRoundedIcon
              aria-hidden
              sx={{
                flexShrink: 0,
                fontSize: { xs: 22, sm: 26 },
                color: tokens.brandInk,
              }}
            />
          ) : null}
        </Stack>

        <ClampedText
          text={String(item.message || '').replace(/,\s*$/, '').trim()}
          lines={2}
          threshold={90}
          onReadMore={undefined}
          sx={{ mb: 1, fontSize: { xs: '0.9375rem', sm: '1rem' } }}
        />
        {storyGone ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {storyGoneMessage(item)} Tap for details.
          </Typography>
        ) : !canOpenCard ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            This is a notice — nothing else to open.
          </Typography>
        ) : null}

        {item.mediaCaption ? (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: 'text.secondary',
              fontStyle: 'italic',
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            }}
          >
            {item.mediaCaption}
          </Typography>
        ) : null}

        {item.kind === 'audio' && item.mediaUrl && (
          <Box sx={{ mb: 1 }} onClick={stopCardClick} onKeyDown={stopCardClick}>
            {item.imageUrl ? (
              <Box
                component="img"
                src={item.imageUrl}
                alt=""
                sx={{
                  display: 'block',
                  width: '100%',
                  maxHeight: { xs: 100, sm: 140 },
                  objectFit: 'cover',
                  borderRadius: 2,
                  mb: 1,
                }}
              />
            ) : null}
            <audio
              controls
              src={item.mediaUrl}
              data-alert-media={item.id}
              style={{ width: '100%' }}
            />
          </Box>
        )}
        {item.kind === 'video' &&
          item.mediaUrl &&
          (/(youtube\.com|youtu\.be|vimeo\.com)/i.test(item.mediaUrl) ? (
            <Stack spacing={1} sx={{ mb: 1 }} onClick={stopCardClick}>
              {item.imageUrl ? (
                <Box
                  component="img"
                  src={item.imageUrl}
                  alt=""
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxHeight: { xs: 120, sm: 180 },
                    objectFit: 'cover',
                    borderRadius: 2,
                  }}
                />
              ) : null}
              <Button
                href={item.mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
              >
                Open video
              </Button>
            </Stack>
          ) : (
            <Box sx={{ mb: 1 }} onClick={stopCardClick} onKeyDown={stopCardClick}>
              <Box
                component="video"
                controls
                src={item.mediaUrl}
                poster={item.imageUrl || undefined}
                data-alert-media={item.id}
                sx={{ width: '100%', borderRadius: 2, maxHeight: { xs: 180, sm: 260 } }}
              />
            </Box>
          ))}

        {otherActions.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            onClick={stopCardClick}
          >
            {otherActions.map((action) => (
              <Button
                key={action.id}
                size="small"
                variant={
                  action.style === 'primary' || item.kind === 'actionable'
                    ? 'contained'
                    : 'outlined'
                }
                color={
                  action.style === 'destructive'
                    ? 'error'
                    : action.style === 'primary'
                      ? 'secondary'
                      : 'primary'
                }
                onClick={() => onAction(item, action)}
              >
                {action.label}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
